import React, { useEffect, useMemo, useState } from 'react';
import {
  Banknote,
  CalendarClock,
  CheckCircle2,
  CreditCard,
  Filter,
  Receipt,
  Search,
  TrendingUp,
  WalletCards
} from 'lucide-react';

const PAGE_SIZE = 50;

const normalizeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const FinancialPaymentsWorkspace = ({
  payments,
  invoices,
  paymentMethods,
  formatDate,
  formatCurrency,
  canCorrectPaymentDate = false,
  onCorrectPaymentDate
}) => {
  const [editingPayment, setEditingPayment] = useState(null);
  const [newPaymentDate, setNewPaymentDate] = useState('');
  const [correctionReason, setCorrectionReason] = useState('');
  const [correctionSaving, setCorrectionSaving] = useState(false);
  const [correctionError, setCorrectionError] = useState('');
  const todayInBrazil = () => new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit'
  }).format(new Date());
  const paymentDayInBrazil = (value) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit'
    }).format(parsed);
  };
  const eligibleForCorrection = (payment) =>
    canCorrectPaymentDate && payment?.status === 'confirmado' &&
    Boolean(payment?.invoice_id) && !payment?.invoice_installment_id &&
    !payment?.subscription_id && payment?.installment_number == null &&
    (payment?.total_installments == null || payment.total_installments === 1) &&
    payment?.payment_method_id != null &&
    invoiceMap.get(payment.invoice_id)?.payment_type !== 'installment' &&
    (invoiceMap.get(payment.invoice_id)?.invoice_type || 'patient') === 'patient';
  const openCorrection = (payment) => {
    setEditingPayment(payment);
    setNewPaymentDate(paymentDayInBrazil(payment.payment_date));
    setCorrectionReason('');
    setCorrectionError('');
  };
  const saveCorrection = async (event) => {
    event.preventDefault();
    if (!editingPayment || correctionSaving) return;
    if (!newPaymentDate || newPaymentDate > todayInBrazil() ||
        newPaymentDate === paymentDayInBrazil(editingPayment.payment_date) ||
        correctionReason.trim().length < 10 || correctionReason.trim().length > 500) {
      setCorrectionError('Informe uma data anterior ou igual a hoje, diferente da atual, e uma justificativa de 10 a 500 caracteres.');
      return;
    }
    setCorrectionSaving(true);
    setCorrectionError('');
    try {
      await onCorrectPaymentDate(editingPayment.id, newPaymentDate, correctionReason.trim());
      setEditingPayment(null);
    } catch (error) {
      setCorrectionError(error?.response?.data?.detail || error?.message || 'Não foi possível corrigir o pagamento.');
    } finally {
      setCorrectionSaving(false);
    }
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [originFilter, setOriginFilter] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const rows = useMemo(
    () => (Array.isArray(payments) ? payments : []),
    [payments]
  );

  const invoiceMap = useMemo(() => {
    const map = new Map();
    (Array.isArray(invoices) ? invoices : []).forEach((invoice) => {
      if (invoice?.id) map.set(invoice.id, invoice);
    });
    return map;
  }, [invoices]);

  const getPaymentOrigin = (payment) => {
    if (!payment?.invoice_id) return 'credit';
    if (payment?.invoice_installment_id) return 'installment';
    return 'invoice';
  };

  const getOriginLabel = (payment) => {
    const origin = getPaymentOrigin(payment);
    const invoice = payment?.invoice_id ? invoiceMap.get(payment.invoice_id) : null;
    const invoiceNumber = invoice?.invoice_number;

    if (origin === 'credit') return 'Crédito antecipado';

    if (origin === 'installment') {
      const installmentLabel =
        payment?.installment_number && payment?.total_installments
          ? `Parcela ${payment.installment_number}/${payment.total_installments}`
          : 'Recebimento de parcela';
      return invoiceNumber
        ? `${installmentLabel} · Fatura ${invoiceNumber}`
        : installmentLabel;
    }

    return invoiceNumber ? `Fatura ${invoiceNumber}` : 'Recebimento de fatura';
  };

  const summary = useMemo(() => {
    const confirmed = rows.filter((payment) => payment?.status === 'confirmado');
    const total = confirmed.reduce(
      (sum, payment) => sum + Number(payment?.amount || 0),
      0
    );

    const methods = confirmed.reduce((acc, payment) => {
      const key = payment?.payment_method_name || 'Não informado';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const mainMethod = Object.entries(methods).sort((a, b) => b[1] - a[1])[0];

    return {
      total,
      count: confirmed.length,
      average: confirmed.length ? total / confirmed.length : 0,
      mainMethod: mainMethod?.[0] || '—',
      mainMethodCount: mainMethod?.[1] || 0
    };
  }, [rows]);

  const methodOptions = useMemo(() => {
    const configured = (Array.isArray(paymentMethods) ? paymentMethods : [])
      .map((method) => ({
        value: method?.id || method?.name,
        label: method?.name || method?.id
      }))
      .filter((method) => method.value && method.label);

    const received = rows
      .map((payment) => ({
        value: payment?.payment_method_id || payment?.payment_method_name,
        label: payment?.payment_method_name || payment?.payment_method_id
      }))
      .filter((method) => method.value && method.label);

    const unique = new Map();
    [...configured, ...received].forEach((method) => {
      if (!unique.has(method.value)) unique.set(method.value, method);
    });

    return Array.from(unique.values()).sort((a, b) =>
      a.label.localeCompare(b.label, 'pt-BR')
    );
  }, [paymentMethods, rows]);

  const filteredPayments = useMemo(() => {
    const query = normalizeText(searchTerm);

    return rows.filter((payment) => {
      const invoice = payment?.invoice_id
        ? invoiceMap.get(payment.invoice_id)
        : null;
      const origin = getPaymentOrigin(payment);
      const methodKey = payment?.payment_method_id || payment?.payment_method_name;

      const searchableText = normalizeText(
        [
          payment?.patient_name,
          payment?.reference,
          payment?.transaction_id,
          payment?.payment_method_name,
          invoice?.invoice_number,
          getOriginLabel(payment)
        ].join(' ')
      );

      const matchesSearch = !query || searchableText.includes(query);
      const matchesStatus = !statusFilter || payment?.status === statusFilter;
      const matchesMethod = !methodFilter || methodKey === methodFilter;
      const matchesOrigin = !originFilter || origin === originFilter;

      return matchesSearch && matchesStatus && matchesMethod && matchesOrigin;
    });
  }, [
    rows,
    invoiceMap,
    searchTerm,
    statusFilter,
    methodFilter,
    originFilter
  ]);

  const visiblePayments = filteredPayments.slice(0, visibleCount);
  const hasActiveFilters = Boolean(
    searchTerm || statusFilter || methodFilter || originFilter
  );

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [searchTerm, statusFilter, methodFilter, originFilter, payments]);

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setMethodFilter('');
    setOriginFilter('');
  };

  const statusLabel = (status) => {
    const labels = {
      confirmado: 'Confirmado',
      pendente: 'Pendente',
      cancelado: 'Cancelado',
      estornado: 'Estornado'
    };
    return labels[status] || status || 'Não informado';
  };

  const statusClass = (status) => {
    const classes = {
      confirmado: 'bg-emerald-100 text-emerald-800',
      pendente: 'bg-amber-100 text-amber-800',
      cancelado: 'bg-slate-100 text-slate-700',
      estornado: 'bg-red-100 text-red-800'
    };
    return classes[status] || 'bg-slate-100 text-slate-700';
  };

  const originClass = (payment) => {
    const classes = {
      credit: 'bg-violet-100 text-violet-800',
      installment: 'bg-blue-100 text-blue-800',
      invoice: 'bg-indigo-100 text-indigo-800'
    };
    return classes[getPaymentOrigin(payment)];
  };

  const summaryCards = [
    {
      label: 'Recebido no período',
      value: formatCurrency(summary.total),
      detail: `${summary.count} recebimento(s) confirmado(s)`,
      icon: Banknote,
      wrapper: 'border-emerald-200 bg-emerald-50',
      iconStyle: 'bg-emerald-100 text-emerald-700'
    },
    {
      label: 'Quantidade',
      value: summary.count,
      detail: 'Lançamentos confirmados',
      icon: CheckCircle2,
      wrapper: 'border-blue-200 bg-blue-50',
      iconStyle: 'bg-blue-100 text-blue-700'
    },
    {
      label: 'Ticket médio',
      value: formatCurrency(summary.average),
      detail: 'Média por recebimento',
      icon: TrendingUp,
      wrapper: 'border-purple-200 bg-purple-50',
      iconStyle: 'bg-purple-100 text-purple-700'
    },
    {
      label: 'Principal forma',
      value: summary.mainMethod,
      detail:
        summary.mainMethodCount > 0
          ? `${summary.mainMethodCount} recebimento(s)`
          : 'Sem recebimentos no período',
      icon: CreditCard,
      wrapper: 'border-slate-200 bg-slate-50',
      iconStyle: 'bg-slate-200 text-slate-700'
    }
  ];

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 bg-gradient-to-r from-slate-50 via-white to-emerald-50 px-5 py-5 sm:px-6">
          <div className="flex items-start gap-4">
            <div className="rounded-2xl bg-emerald-600 p-3 text-white shadow-sm">
              <WalletCards className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">
                Controle de entradas
              </p>
              <h3 className="mt-1 text-2xl font-bold text-slate-950">
                Recebimentos
              </h3>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-600">
                Consulte pagamentos de faturas, parcelas e créditos antecipados no período selecionado.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 sm:p-5 xl:grid-cols-4">
          {summaryCards.map((card) => {
            const Icon = card.icon;
            return (
              <div
                key={card.label}
                className={`rounded-xl border p-4 ${card.wrapper}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                      {card.label}
                    </p>
                    <p className="mt-2 truncate text-xl font-bold text-slate-950">
                      {card.value}
                    </p>
                    <p className="mt-1 text-xs text-slate-600">{card.detail}</p>
                  </div>
                  <div className={`rounded-xl p-2.5 ${card.iconStyle}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 sm:p-5">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <div className="md:col-span-2 xl:col-span-1">
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-600">
              Pesquisar
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Paciente, fatura ou referência..."
                className="w-full rounded-xl border border-slate-300 bg-white py-3 pl-10 pr-3 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              />
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-600">
              Forma de pagamento
            </label>
            <select
              value={methodFilter}
              onChange={(event) => setMethodFilter(event.target.value)}
              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-3 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            >
              <option value="">Todas as formas</option>
              {methodOptions.map((method) => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-600">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-3 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            >
              <option value="">Todos os status</option>
              <option value="confirmado">Confirmado</option>
              <option value="pendente">Pendente</option>
              <option value="cancelado">Cancelado</option>
              <option value="estornado">Estornado</option>
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-600">
              Origem
            </label>
            <select
              value={originFilter}
              onChange={(event) => setOriginFilter(event.target.value)}
              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-3 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            >
              <option value="">Todas as origens</option>
              <option value="invoice">Fatura</option>
              <option value="installment">Parcela</option>
              <option value="credit">Crédito antecipado</option>
            </select>
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="inline-flex w-fit items-center rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-600">
            <Filter className="mr-2 h-4 w-4 text-emerald-600" />
            <span className="font-semibold text-slate-900">
              {filteredPayments.length}
            </span>
            <span className="ml-1">de {rows.length}</span>
          </div>

          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100"
            >
              Limpar filtros
            </button>
          )}
        </div>
      </section>

      {filteredPayments.length === 0 ? (
        <section className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
            <Receipt className="h-6 w-6" />
          </div>
          <h4 className="mt-4 text-lg font-semibold text-slate-900">
            {hasActiveFilters
              ? 'Nenhum recebimento corresponde aos filtros'
              : 'Nenhum recebimento encontrado'}
          </h4>
          <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-600">
            {hasActiveFilters
              ? 'Ajuste ou limpe os filtros para visualizar outros lançamentos.'
              : 'Os pagamentos registrados nas faturas e os créditos antecipados aparecerão aqui.'}
          </p>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="mt-5 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700"
            >
              Limpar filtros
            </button>
          )}
        </section>
      ) : (
        <>
          <section className="hidden overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm md:block">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    {['Data', 'Paciente', 'Origem', 'Valor', 'Método', 'Status', ...(canCorrectPaymentDate ? ['Ações'] : [])].map(
                      (title) => (
                        <th
                          key={title}
                          className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
                        >
                          {title}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {visiblePayments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-slate-50/80">
                      <td className="whitespace-nowrap px-5 py-4 text-sm text-slate-700">
                        {formatDate(payment.payment_date)}
                      </td>
                      <td className="px-5 py-4">
                        <div className="text-sm font-semibold text-slate-900">
                          {payment.patient_name || 'Paciente não informado'}
                        </div>
                        {payment.reference && (
                          <div className="mt-1 text-xs text-slate-500">
                            Ref: {payment.reference}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${originClass(
                            payment
                          )}`}
                        >
                          {getOriginLabel(payment)}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-sm font-bold text-emerald-700">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="px-5 py-4">
                        <div className="text-sm font-medium text-slate-900">
                          {payment.payment_method_name || 'Não informado'}
                        </div>
                        {payment.transaction_id && (
                          <div className="mt-1 text-xs text-slate-500">
                            ID: {payment.transaction_id}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass(
                            payment.status
                          )}`}
                        >
                          {statusLabel(payment.status)}
                        </span>
                      </td>
                      {canCorrectPaymentDate && (
                        <td className="px-5 py-4 text-sm">
                          {eligibleForCorrection(payment) && (
                            <button type="button" onClick={() => openCorrection(payment)}
                              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 font-medium text-blue-700 hover:bg-blue-50">
                              <CalendarClock size={15} /> Corrigir data
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="space-y-3 md:hidden">
            {visiblePayments.map((payment) => (
              <article
                key={payment.id}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-950">
                      {payment.patient_name || 'Paciente não informado'}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      {formatDate(payment.payment_date)}
                    </p>
                  </div>
                  <p className="whitespace-nowrap text-base font-bold text-emerald-700">
                    {formatCurrency(payment.amount)}
                  </p>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <span
                    className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${originClass(
                      payment
                    )}`}
                  >
                    {getOriginLabel(payment)}
                  </span>
                  <span
                    className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass(
                      payment.status
                    )}`}
                  >
                    {statusLabel(payment.status)}
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 border-t border-slate-100 pt-3 text-xs">
                  <div>
                    <p className="text-slate-500">Forma</p>
                    <p className="mt-1 font-semibold text-slate-800">
                      {payment.payment_method_name || 'Não informado'}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500">Referência</p>
                    <p className="mt-1 truncate font-semibold text-slate-800">
                      {payment.reference || payment.transaction_id || '—'}
                    </p>
                  </div>
                </div>
                {eligibleForCorrection(payment) && (
                  <button type="button" onClick={() => openCorrection(payment)}
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-blue-700">
                    <CalendarClock size={16} /> Corrigir data de recebimento
                  </button>
                )}
              </article>
            ))}
          </section>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-slate-600">
              Exibindo{' '}
              <span className="font-semibold text-slate-900">
                {Math.min(visibleCount, filteredPayments.length)}
              </span>{' '}
              de{' '}
              <span className="font-semibold text-slate-900">
                {filteredPayments.length}
              </span>{' '}
              recebimentos
            </p>

            {visibleCount < filteredPayments.length && (
              <button
                type="button"
                onClick={() => setVisibleCount((current) => current + PAGE_SIZE)}
                className="inline-flex items-center justify-center rounded-xl border border-emerald-200 bg-white px-4 py-2.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"
              >
                Mostrar mais
              </button>
            )}
          </div>
        </>
      )}
      {editingPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="presentation">
          <div role="dialog" aria-modal="true" aria-labelledby="correction-title"
            className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
            <h3 id="correction-title" className="text-lg font-bold text-slate-900">Corrigir data de recebimento</h3>
            <p className="mt-2 text-sm text-slate-600">
              {editingPayment.patient_name || 'Paciente'} · {formatCurrency(editingPayment.amount)}
            </p>
            <p className="mt-1 text-xs text-slate-500">Data registrada: {formatDate(editingPayment.payment_date)}</p>
            <p className="mt-2 text-xs text-amber-800">Esta operação altera apenas a data do pagamento e fica registrada na auditoria.</p>
            <form onSubmit={saveCorrection} className="mt-4 space-y-4">
              <label className="block text-sm font-medium text-slate-700">
                Nova data de recebimento
                <input type="date" required value={newPaymentDate} max={todayInBrazil()}
                  onChange={(event) => setNewPaymentDate(event.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-300 p-2" />
              </label>
              <label className="block text-sm font-medium text-slate-700">
                Justificativa (obrigatória)
                <textarea required minLength={10} maxLength={500} rows={3}
                  value={correctionReason} onChange={(event) => setCorrectionReason(event.target.value)}
                  placeholder="Ex.: Pagamento realizado anteriormente ao lançamento da fatura"
                  className="mt-1 w-full rounded-lg border border-slate-300 p-2" />
              </label>
              {correctionError && <p role="alert" className="text-sm text-red-700">{correctionError}</p>}
              <div className="flex justify-end gap-2">
                <button type="button" disabled={correctionSaving} onClick={() => setEditingPayment(null)}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm">Cancelar</button>
                <button type="submit" disabled={correctionSaving}
                  className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                  {correctionSaving ? 'Salvando...' : 'Confirmar correção'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialPaymentsWorkspace;
